import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { subscribeToProfile } from '../services/profileService';
import { subscribeToSettings } from '../services/settingsService';
import { updateDeliverySettings } from '../utils/deliverySlots';
import { ROUTES } from '../navigation';

// Cart persistence helpers (2-day expiration)
const CART_STORAGE_KEY = 'flash_cart';
const CART_EXPIRY_DAYS = 2;

const loadCartFromStorage = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) return [];
    
    const { cart, timestamp } = JSON.parse(stored);
    const expiryTime = CART_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    
    if (Date.now() - timestamp > expiryTime) {
      localStorage.removeItem(CART_STORAGE_KEY);
      return [];
    }
    return cart || [];
  } catch {
    return [];
  }
};

const saveCartToStorage = (cart) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
      cart,
      timestamp: Date.now()
    }));
  } catch {
    // Storage full or unavailable
  }
};

const CATEGORY_ALIASES = {
  Groceries: 'Grocery',
  'Dairy & Eggs': 'Dairy',
  'Fruits & Vegetables': 'Vegetables',
};

const normalizeCategory = (category) => CATEGORY_ALIASES[category] || category || '';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [cart, setCart] = useState(() => loadCartFromStorage());
  const [userAddresses, setUserAddresses] = useState([]);
  const [userName, setUserName] = useState('');
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);
  const [settings, setSettings] = useState(null);
  const [trackingOrderId, setTrackingOrderId] = useState(null);
  const [hideBottomNav, setHideBottomNav] = useState(false);
  const headerRef = useRef(null);

  // Measure header height and set as CSS variable
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        const h = headerRef.current.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-height', `${h}px`);
      }
    };
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  // Subscribe to settings from admin
  useEffect(() => {
    const unsubscribe = subscribeToSettings((newSettings) => {
      setSettings(newSettings);
      updateDeliverySettings(newSettings);
    });
    return () => unsubscribe();
  }, []);

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    saveCartToStorage(cart);
  }, [cart]);

  // Listen for initial auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Subscribe to profile
  useEffect(() => {
    if (!currentUser?.uid) {
      setProfileLoading(false);
      return () => {};
    }
    setProfileLoading(true);
    const unsub = subscribeToProfile(currentUser.uid, (data) => {
      if (data) {
        if (data.addresses) {
          setUserAddresses(data.addresses);
        }
        if (data.name) {
          setUserName(data.name);
          setNeedsProfileSetup(false);
        } else {
          setNeedsProfileSetup(true);
        }
      } else {
        setNeedsProfileSetup(true);
      }
      setProfileLoading(false);
    });
    return () => unsub();
  }, [currentUser]);

  // Fetch products
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'products'),
      (snapshot) => {
        const fetchedProducts = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            category: normalizeCategory(data.category),
            price: Number(data.price) || 0,
            originalPrice: data.originalPrice ? Number(data.originalPrice) : null,
            stock: Number(data.stock) || 0,
          };
        });
        setProducts(fetchedProducts);
        setProductsLoading(false);
      },
      (error) => {
        console.error('Error fetching products:', error);
        setProductsLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const handleAuthReady = useCallback((user) => {
    setCurrentUser(user);
  }, []);

  const handleAddToCart = useCallback((product) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === product.id);
      if (existingItem) {
        return prevCart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
  }, []);

  const handleRemoveFromCart = useCallback((productId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
  }, []);

  const handleUpdateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
      return;
    }
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  }, []);

  const handleClearCart = useCallback(() => {
    setCart([]);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      setUserAddresses([]);
      setUserName('');
      navigate(ROUTES.HOME, { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [navigate]);

  // Computed values
  const cartQty = useMemo(() => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  }, [cart]);

  const subtotal = useMemo(() => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  }, [cart]);

  const minOrderForFreeDelivery = settings?.minOrderValue || 0;

  const value = {
    // State
    products,
    productsLoading,
    currentUser,
    authLoading,
    profileLoading,
    cart,
    userAddresses,
    userName,
    needsProfileSetup,
    setNeedsProfileSetup,
    settings,
    trackingOrderId,
    setTrackingOrderId,
    hideBottomNav,
    setHideBottomNav,
    headerRef,
    // Computed
    cartQty,
    subtotal,
    minOrderForFreeDelivery,
    // Actions
    handleAuthReady,
    handleAddToCart,
    handleRemoveFromCart,
    handleUpdateQuantity,
    handleClearCart,
    handleLogout,
    setCurrentUser,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return ctx;
}

export default AppContext;
